import { useMemo, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Box,
  Group,
  ScrollArea,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import {
  IconArrowDown,
  IconArrowUp,
  IconEye,
  IconFile,
  IconFolder,
  IconLink,
  IconLock,
  IconSearch,
} from '@tabler/icons-react'
import { type FileItem, formatFileSize } from './types'

export interface FileListProps {
  items: FileItem[]
  currentPath: string
  onNavigate: (path: string) => void
  onPreview: (item: FileItem) => void
}

type SortField = 'name' | 'size' | 'modified'
type SortDir = 'asc' | 'desc'

function permissionColor(permissions: string): string {
  if (permissions.includes('x')) return 'green'
  if (permissions === '600' || permissions === '400') return 'orange'
  return 'blue'
}

function fileIcon(item: FileItem) {
  if (item.type === 'directory') return <IconFolder size={14} color="var(--mantine-color-yellow-6)" />
  if (item.type === 'symlink') return <IconLink size={14} color="var(--mantine-color-blue-6)" />
  if (item.blocked) return <IconLock size={14} color="var(--mantine-color-orange-6)" />
  return <IconFile size={14} color="var(--mantine-color-gray-6)" />
}

function compareFn(field: SortField, dir: SortDir) {
  const mult = dir === 'asc' ? 1 : -1
  return (a: FileItem, b: FileItem): number => {
    if (field === 'name') return a.name.localeCompare(b.name) * mult
    if (field === 'size') return (a.size - b.size) * mult
    return a.modified.localeCompare(b.modified) * mult
  }
}

export function FileList({ items, currentPath, onNavigate, onPreview }: FileListProps) {
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const isHomeUbuntu = currentPath === '/home/ubuntu' || currentPath === '/home/ubuntu/'

  const sorted = useMemo(() => {
    const q = search.toLowerCase()
    const filtered = items
      .filter((i) => i.name !== '.' && i.name !== '..')
      .map((i) => {
        if (!i.blocked && isHomeUbuntu && i.type === 'file' && Number(i.permissions) < 755) {
          return { ...i, blocked: true }
        }
        return i
      }).filter((i) => i.name.toLowerCase().includes(q))
    const dirs = filtered.filter((i) => i.type === 'directory')
    const files = filtered.filter((i) => i.type !== 'directory')
    const cmp = compareFn(sortField, sortDir)
    dirs.sort(cmp)
    files.sort(cmp)
    return [...dirs, ...files]
  }, [items, search, sortField, sortDir, isHomeUbuntu])

  const dirCount = sorted.filter((i) => i.type === 'directory').length
  const fileCount = sorted.filter((i) => i.type !== 'directory').length
  const blockedCount = sorted.filter((i) => i.blocked).length

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('asc')
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return null
    return sortDir === 'asc' ? <IconArrowUp size={10} /> : <IconArrowDown size={10} />
  }

  function handleNavigate(name: string) {
    const base = currentPath.replace(/\/$/, '')
    onNavigate(`${base}/${name}`)
  }

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <Group justify="space-between" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }} p="xs">
        <Text size="xs" c="dimmed" ff="monospace">
          {currentPath}
        </Text>
        <TextInput
          leftSection={<IconSearch size={13} />}
          w={160}
          size="xs"
          placeholder="Buscar\u2026"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </Group>

      {/* Table */}
      <ScrollArea style={{ flex: 1 }}>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Group gap={4} style={{ cursor: 'pointer' }} onClick={() => toggleSort('name')}>
                  <Text size="xs" fw={600}>Nome</Text>
                  <SortIcon field="name" />
                </Group>
              </Table.Th>
              <Table.Th w={80}>
                <Group gap={4} style={{ cursor: 'pointer' }} onClick={() => toggleSort('size')}>
                  <Text size="xs" fw={600}>Tamanho</Text>
                  <SortIcon field="size" />
                </Group>
              </Table.Th>
              <Table.Th w={100}>
                <Text size="xs" fw={600}>Permiss&#xF5;es</Text>
              </Table.Th>
              <Table.Th w={140}>
                <Group gap={4} style={{ cursor: 'pointer' }} onClick={() => toggleSort('modified')}>
                  <Text size="xs" fw={600}>Modificado</Text>
                  <SortIcon field="modified" />
                </Group>
              </Table.Th>
              <Table.Th w={40}>
                <Text size="xs" fw={600}>A&#xE7;&#xE3;o</Text>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((item) => (
              <Table.Tr key={item.name} style={item.blocked ? { opacity: 0.6 } : undefined}>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    {fileIcon(item)}
                    {item.type === 'directory' ? (
                      <Text
                        size="sm"
                        c="blue"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleNavigate(item.name)}
                      >
                        {item.name}
                      </Text>
                    ) : (
                      <Text size="sm">{item.name}</Text>
                    )}
                    {item.type === 'symlink' && item.link_target && (
                      <Text size="xs" c="dimmed">&rarr; {item.link_target}</Text>
                    )}
                    {item.blocked && (
                      <Badge size="xs" color="orange" variant="light" ml={4}>bloqueado</Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ta="right">
                    {item.type === 'directory' ? '\u2014' : formatFileSize(item.size)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace" c={permissionColor(item.permissions)}>
                    {item.permissions}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{item.modified}</Text>
                </Table.Td>
                <Table.Td>
                  {!item.blocked && item.type !== 'directory' && (
                    <Tooltip label="Visualizar">
                      <ActionIcon variant="subtle" size="sm" onClick={() => onPreview(item)}>
                        <IconEye size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      {/* Footer */}
      <Group justify="space-between" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }} p="xs">
        <Text size="xs" c="dimmed">
          {sorted.length} itens ({dirCount} pastas, {fileCount} arquivos{blockedCount > 0 ? `, ${blockedCount} bloqueados` : ''})
        </Text>
        <Group gap={4}>
          <IconLock size={12} />
          <Text size="xs" c="dimmed">Somente leitura</Text>
        </Group>
      </Group>
    </Box>
  )
}
